import { RemoteMicrositeConfig } from 'types/RemoteMicrositeConfig';

// Session-scoped cache: persists across re-renders, cleared on full page reload.
// Only successful fetches are cached — errors and malformed JSON return undefined
// and will be retried on the next call.
const cache = new Map<string, RemoteMicrositeConfig>();

const TIMEOUT_MS = 3000;
const CONFIG_SUFFIX = '_live.json'; // All live configs are suffixed with this string

export async function fetchRemoteConfig(
  promoId: string,
  baseUrl: string
): Promise<RemoteMicrositeConfig | undefined> {
  if (cache.has(promoId)) {
    return cache.get(promoId);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const normalizedBaseUrl = baseUrl.replace(/\/+$/, '');
    const response = await fetch(
      `${normalizedBaseUrl}/${promoId}${CONFIG_SUFFIX}`,
      {
        signal: controller.signal,
      }
    );

    if (!response.ok) {
      console.warn(
        `[remoteConfig] fetch failed for ${promoId}: ${response.status}`
      );
      return undefined;
    }

    const data: RemoteMicrositeConfig = await response.json();
    cache.set(promoId, data);
    return data;
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      console.warn(`[remoteConfig] fetch timed out for ${promoId}`);
    } else {
      console.warn(`[remoteConfig] fetch error for ${promoId}:`, err);
    }
    return undefined;
  } finally {
    clearTimeout(timeoutId);
  }
}
